import React, { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import { v4 as uuidv4 } from "uuid";
import randomColor from "randomcolor";
import useElementSize from "../../hooks/useElementSize";
import { db } from "../../firebase/client";
import {
  subscribeBoardMessages,
  writeBoardMessage,
  deleteBoardItem,
  logContentDelete
} from "../../utils/boardFirestore";
import {
  deviceTimeZone, wallClockToInstant, instantToWallClock, formatReminder, isMessageVisible,
  alarmState, formatAlarm, DAY_MS, MINUTE_MS, HOUR_MS, DEFAULT_WARN_MS,
} from "../../utils/time";
import useResidentPhotos from "../../hooks/useResidentPhotos";
import { addResidentPhoto, deleteResidentPhoto, updateResidentPhotoCaption } from "../../utils/photo";
import PhotoSlideshow from "../PhotoSlideshow/PhotoSlideshow";
import BigClock from "../BigClock/BigClock";

const clamp01 = (n) => Math.min(1, Math.max(0, n));
// A pointer move under this many px counts as a click (edit), not a drag.
const DRAG_THRESHOLD = 5;
// When the caregiver turns scheduling on, default "Show from" to now + this lead
// so a just-typed message doesn't flash onto the resident's tablet while they're
// still filling in the schedule. They can always dial it back to the present.
const SCHEDULE_LEAD_MS = 60 * 1000;

// --- The board is a single WYSIWYG canvas -----------------------------------
// Render the board at the tablet's logical board-area size, then CSS-scale it to
// fit the family member's screen, so the family always sees and edits exactly
// what the resident sees (same aspect ratio + photo split, so notes can't
// secretly overlap or cover a photo). Drag to move, click to edit.
//
// The 10.1" panel is 1280x800 (TABLET_PANEL). Notes live in the board AREA below
// the name + date banners, so the coordinate space fractional positions map onto
// is shorter than the full panel: RESIDENT_BOARD ~= panel minus ~130px of
// banners. TABLET_PANEL drives the vh/vw note sizing (Display's fonts + padding
// are vh of the whole viewport; max-width is 40vw); RESIDENT_BOARD is the note
// coordinate space. The board height is an estimate until the kiosk reports its
// own measured board area (see resident-view-preview backlog) — the exact fix.
const TABLET_PANEL = { w: 1280, h: 800 };
const RESIDENT_BOARD = { w: 1280, h: 670 };
// Display note font sizes as vh NUMBERS (mirror DISPLAY_FONT in Display.js:
// small 3.5vh / medium 5vh / large 6.5vh), converted to px against the panel.
const DISPLAY_FONT_VH = { small: 3.5, medium: 5, large: 6.5 };
const vhPx = (vh) => (vh / 100) * TABLET_PANEL.h;  // vh of the panel -> px
// Big-clock TIME font size (vh) per placement — mirrors the room display's CSS.
const CLOCK_TIME_VH = { full: 22, left: 15, right: 8.5 };

// Tidy auto-placement: fractional (0..1) top-left anchors on a staggered grid
// with margins, so the resident's screen stays neat no matter where a note was
// created (phone list, desktop canvas) and without anyone dragging. A note gets
// the first grid slot not already occupied by an existing note; once every slot
// is taken, we wrap around (rare — the room screen is full well before then).
const GRID = { cols: 3, rows: 5, x0: 0.04, y0: 0.05, dx: 0.32, dy: 0.185 };
const SLOTS = GRID.cols * GRID.rows;
// Column-major fill: each new note stacks straight DOWN below the previous one
// (same column, next row), only stepping to the next column once a column is
// full. Notes are wide, so vertical separation avoids horizontal overlap.
const slotPos = (i) => ({
  x: clamp01(GRID.x0 + Math.floor(i / GRID.rows) * GRID.dx),
  y: clamp01(GRID.y0 + (i % GRID.rows) * GRID.dy)
});
// Which slot a note's current position falls into (nearest cell).
const slotOf = (pos) => {
  const col = Math.min(GRID.cols - 1, Math.max(0, Math.round(((pos?.x ?? 0) - GRID.x0) / GRID.dx)));
  const row = Math.min(GRID.rows - 1, Math.max(0, Math.round(((pos?.y ?? 0) - GRID.y0) / GRID.dy)));
  return col * GRID.rows + row;
};
const nextFreeSlot = (items) => {
  const taken = new Set(items.map((n) => slotOf(n.pos)));
  for (let i = 0; i < SLOTS; i++) if (!taken.has(i)) return slotPos(i);
  return slotPos(items.length % SLOTS); // all full: wrap
};

// How long a scheduled event stays visible (duration -> endAt). "Always" = open-ended.
const DURATIONS = [
  { key: "1d", label: "1 day", ms: DAY_MS },
  { key: "3d", label: "3 days", ms: 3 * DAY_MS },
  { key: "1w", label: "1 week", ms: 7 * DAY_MS },
  { key: "2w", label: "2 weeks", ms: 14 * DAY_MS },
  { key: "1m", label: "1 month", ms: 30 * DAY_MS },
  { key: "always", label: "Always", ms: null },
];
const DURATION_MS = (key) => (DURATIONS.find((d) => d.key === key) || DURATIONS[0]).ms;
// Nearest duration key for an existing start/end pair (for the edit form).
const durationKeyFor = (startAt, endAt) => {
  if (startAt == null || endAt == null) return "always";
  const ms = endAt - startAt;
  let best = DURATIONS[0];
  for (const d of DURATIONS) if (d.ms != null && Math.abs(d.ms - ms) < Math.abs((best.ms ?? Infinity) - ms)) best = d;
  return best.key;
};
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const toggleDay = (arr, d) => (arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d].sort((a, b) => a - b));

// Short "when will this show" label for a scheduled note not currently visible.
const scheduleLabel = (note, now, tz) => {
  if (note.startAt == null) return "";
  const recur = note.recur || "once";
  const timeStr = new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(new Date(note.startAt));
  if (recur === "once") {
    if (note.endAt != null && now > note.endAt) return "ended";
    return `shows ${formatReminder(note.startAt, tz)}`;
  }
  if (recur === "daily") return `shows daily · ${timeStr}`;
  if (recur === "weekly") {
    const days = (note.recurDays || []).map((d) => WEEKDAYS[d]).join(" ");
    return `shows ${days || "weekly"} · ${timeStr}`;
  }
  return "scheduled";
};

const CaregiverBoard = ({ boardId, tenantId, authorUid, authorName, fontSize = "small", residentTimezone, cellular = false, composeOpen = true, onComposeClose, staleAfterDays = 0, residentCreatedAt = 0, residentName }) => {
  const tz = residentTimezone || deviceTimeZone();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [rvRef, rvSize] = useElementSize(); // width of the board frame -> scale
  const rvK = (rvSize.width || RESIDENT_BOARD.w) / RESIDENT_BOARD.w;
  // Pointer position at drag start, to tell a real drag from a click-to-edit.
  const startRef = useRef({ x: 0, y: 0 });

  // Live clock so reminders flip to warning/past on their own.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  // Reminder controls for the ADD bar.
  // Schedule controls for the ADD bar (a message is "scheduled" when on).
  const [reminderOn, setReminderOn] = useState(false);
  const [actionLocal, setActionLocal] = useState("");
  const [durationKey, setDurationKey] = useState("1d");
  const [recur, setRecur] = useState("once");
  const [recurDays, setRecurDays] = useState([]);
  const [alertOn, setAlertOn] = useState(false);
  const [eventLocal, setEventLocal] = useState("");   // alarm event time
  const [warnQty, setWarnQty] = useState(30);         // pre-event warning (glow) lead
  const [warnUnit, setWarnUnit] = useState("minutes");

  // Schedule controls inside the edit popup.
  const [editReminderOn, setEditReminderOn] = useState(false);
  const [editActionLocal, setEditActionLocal] = useState("");
  const [editDurationKey, setEditDurationKey] = useState("1d");
  const [editRecur, setEditRecur] = useState("once");
  const [editRecurDays, setEditRecurDays] = useState([]);
  const [editAlertOn, setEditAlertOn] = useState(false);
  const [editEventLocal, setEditEventLocal] = useState("");
  const [editWarnQty, setEditWarnQty] = useState(30);
  const [editWarnUnit, setEditWarnUnit] = useState("minutes");

  // Resident photo slideshow (0..N per resident): shown in the right 40% panel,
  // notes reflow into the left 60%. Any family member may add a slide; clicking a
  // slide opens a delete dialog.
  const photos = useResidentPhotos(boardId, tenantId);
  const fileRef = useRef(null);
  const [photoErr, setPhotoErr] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null); // slide open in the photo dialog
  const [captionDraft, setCaptionDraft] = useState("");

  const shownPhotoActive = photos.length > 0;
  // Mirror the resident screen: a big clock fills the space when the resident
  // wouldn't currently see both messages and photos. "Visible" uses the same
  // rule as the room display, so dimmed/scheduled notes (which the caregiver
  // board still shows) don't count — they overlay the clock at their positions.
  const anyVisible = items.some((n) => isMessageVisible(n, now, tz));
  const clockMode = !anyVisible && !shownPhotoActive ? "full"
    : !anyVisible && shownPhotoActive ? "left"
    : anyVisible && !shownPhotoActive ? "right"
    : null;
  const scaleX = (shownPhotoActive || clockMode === "right") ? 0.6 : 1;

  // Inactivity banner: when the admin set an "Inactivity alert (days)" and the
  // board has had no new message or photo for that long, nudge whoever's viewing
  // to post something. Mirrors the server's staleBoardsTick, computed live here.
  const tsMs = (t) => (t && t.toMillis ? t.toMillis() : (typeof t === "number" ? t : 0));
  let lastActivity = residentCreatedAt || 0;
  for (const n of items) lastActivity = Math.max(lastActivity, tsMs(n.updatedAt), tsMs(n.createdAt));
  for (const p of photos) lastActivity = Math.max(lastActivity, tsMs(p.createdAt));
  const DAY_MS_LOCAL = 24 * 60 * 60 * 1000;
  const staleDays = staleAfterDays > 0 && lastActivity > 0
    ? Math.floor((now - lastActivity) / DAY_MS_LOCAL) : 0;
  const isStale = staleAfterDays > 0 && lastActivity > 0 && staleDays >= staleAfterDays;

  const onPickPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setPhotoErr(null);
    const caption = window.prompt("Add a caption for this photo (optional):", "") || "";
    try {
      await addResidentPhoto({ tenantId, residentId: boardId, file, authorUid, authorName, caption });
    } catch (err) {
      console.error("photo upload failed:", err);
      setPhotoErr(`Could not add photo: ${err && (err.code || err.message) ? (err.code || err.message) : "unknown error"}`);
    }
  };
  const openPhotoDialog = (p) => { setEditPhoto(p); setCaptionDraft(p.caption || ""); };
  const closePhotoDialog = () => { setEditPhoto(null); setCaptionDraft(""); };
  const saveCaption = async () => {
    const p = editPhoto;
    if (!p) return;
    closePhotoDialog();
    try { await updateResidentPhotoCaption({ residentId: boardId, photoId: p.id, caption: captionDraft, authorUid, authorName }); }
    catch (err) { setPhotoErr("Could not save caption."); }
  };
  const deleteThisPhoto = async () => {
    const p = editPhoto;
    if (!p) return;
    closePhotoDialog();
    try {
      await deleteResidentPhoto({ residentId: boardId, photoId: p.id, storagePath: p.storagePath });
      logContentDelete(db, boardId, { tenantId, authorUid, authorName, type: "photo" });
    }
    catch (err) { setPhotoErr("Could not delete photo."); }
  };

  useEffect(() => {
    if (!boardId) {
      setItems([]);
      return;
    }
    const unsubscribe = subscribeBoardMessages(
      db, boardId, setItems,
      (err) => console.error("board subscribe failed:", err),
      tenantId // caregiver reads must filter by tenantId (see boardFirestore/rules)
    );
    return unsubscribe;
  }, [boardId, tenantId]);

  const addNote = () => {
    const value = text.trim();
    if (!value) return;
    const note = {
      id: uuidv4(),
      item: value,
      color: randomColor({ luminosity: "light" }),
      pos: nextFreeSlot(items)
    };
    const sched = scheduleFields({
      on: reminderOn, local: actionLocal, durKey: durationKey, rc: recur, days: recurDays,
      alert: alertOn, eventLoc: eventLocal, warnMs: warnToMs(warnQty, warnUnit),
    });
    if (sched) Object.assign(note, sched);
    writeBoardMessage(db, boardId, note, { tenantId, authorUid, authorName });
    setText("");
    setActionLocal(""); // avoid accidentally reusing the same time on the next note
    if (onComposeClose) onComposeClose(); // collapse the mobile FAB composer after posting
  };

  // Warn interval (qty + unit) <-> milliseconds.
  const warnToMs = (qty, unit) => Math.max(0, Number(qty) || 0) * (unit === "hours" ? HOUR_MS : MINUTE_MS);
  const splitWarn = (ms) => {
    const m = ms == null ? DEFAULT_WARN_MS : ms;
    if (m >= HOUR_MS && m % HOUR_MS === 0) return { qty: m / HOUR_MS, unit: "hours" };
    return { qty: Math.round(m / MINUTE_MS), unit: "minutes" };
  };

  // Build a message's schedule fields from the form, or null if scheduling is
  // off / no start time. When "alert" is on the message is an ALARM: it appears
  // (plain) at startAt, its border glows once now reaches (event − warn), and it
  // greys out at the event time (actionAt); repeat/duration control appear/hide.
  const scheduleFields = ({ on, local, durKey, rc, days, alert, eventLoc, warnMs }) => {
    if (!on || !local) return null;
    const startAt = wallClockToInstant(local, tz);
    if (!startAt) return null;
    const durMs = DURATION_MS(durKey);
    const f = {
      startAt,
      endAt: durMs == null ? null : startAt + durMs,
      recur: rc,
      recurDays: rc === "weekly" ? days : [],
      alert: !!alert,
    };
    if (alert) {
      f.actionAt = eventLoc ? wallClockToInstant(eventLoc, tz) : startAt; // event (grey) time
      f.warnMs = warnMs != null ? warnMs : DEFAULT_WARN_MS;               // glow lead
    }
    return f;
  };
  // Strip every schedule field (turning a scheduled message back into a plain one).
  const clearSchedule = (o) => {
    delete o.startAt; delete o.endAt; delete o.recur; delete o.recurDays; delete o.alert;
    delete o.actionAt; delete o.warnMs; delete o.notifyAt; delete o.notified;
  };

  const onInputKeyDown = (e) => {
    if (e.key === "Enter") addNote();
  };

  // Drag maps back to a fractional position against the logical tablet board
  // size (the canvas is CSS-scaled, so react-draggable's scale prop keeps
  // data.x/y in these unscaled logical coordinates).
  const onDragStopRV = (note, data) => {
    const pos = {
      x: clamp01((data.x / RESIDENT_BOARD.w) / scaleX),
      y: clamp01(data.y / RESIDENT_BOARD.h)
    };
    writeBoardMessage(db, boardId, { ...note, pos }, { tenantId, authorUid, authorName });
  };

  const beginEdit = (note) => {
    setEditingId(note.id);
    setEditText(note.item);
    // An "event" = a scheduled note (startAt) or a legacy reminder (actionAt).
    const start = note.startAt != null ? note.startAt : note.actionAt;
    const isEvent = start != null;
    setEditReminderOn(isEvent);
    setEditActionLocal(isEvent ? instantToWallClock(start, tz) : "");
    setEditDurationKey(note.startAt != null ? durationKeyFor(note.startAt, note.endAt) : "1d");
    setEditRecur(note.recur || "once");
    setEditRecurDays(Array.isArray(note.recurDays) ? note.recurDays : []);
    setEditAlertOn(!!note.alert);
    setEditEventLocal(instantToWallClock(note.actionAt != null ? note.actionAt : start, tz));
    const w = splitWarn(note.warnMs);
    setEditWarnQty(w.qty);
    setEditWarnUnit(w.unit);
  };

  const saveEdit = (note) => {
    const value = editText.trim() || note.item;
    setEditingId(null);
    const updated = { ...note, item: value };
    const sched = scheduleFields({
      on: editReminderOn, local: editActionLocal, durKey: editDurationKey, rc: editRecur, days: editRecurDays,
      alert: editAlertOn, eventLoc: editEventLocal, warnMs: warnToMs(editWarnQty, editWarnUnit),
    });
    if (sched) Object.assign(updated, sched);
    else clearSchedule(updated); // schedule turned off -> plain persistent note
    writeBoardMessage(db, boardId, updated, { tenantId, authorUid, authorName });
  };

  const removeNote = (note) => {
    setEditingId(null);
    deleteBoardItem(db, boardId, note.id);
    logContentDelete(db, boardId, {
      tenantId, authorUid, authorName,
      type: (note.alert || note.actionAt != null) ? "alarm" : "message",
    });
  };
  const cancelEdit = () => setEditingId(null);
  const editingNote = editingId ? items.find((n) => n.id === editingId) : null;

  // A note on the board, styled exactly like Display's .display-note but with its
  // viewport-relative sizes converted to px against the logical board so it
  // matches the resident's proportions. Drag to move; a plain click opens the
  // edit modal (text + reminder + delete).
  const renderRvNote = (note) => {
    const visible = isMessageVisible(note, now, tz);
    const offscreen = note.startAt != null && !visible; // scheduled but not showing now
    // The caregiver board always shows scheduled messages (dimmed + a "shows
    // when" label) so nothing vanishes; the resident's screen hides them (below).
    // Alarm lifecycle while visible: plain -> warning (glow) -> past (grey).
    const st = visible ? alarmState(note, now, tz) : "none";
    const cls = "display-note"
      + (st === "warning" ? " reminder-warn" : st === "past" ? " reminder-past" : "")
      + (offscreen ? " rv-offscreen" : "");
    const fh = DISPLAY_FONT_VH[fontSize] || DISPLAY_FONT_VH.small;
    return (
      <Draggable
        key={note.id}
        bounds="parent"
        scale={rvK}
        position={{
          x: (note.pos?.x ?? 0.4) * scaleX * RESIDENT_BOARD.w,
          y: (note.pos?.y ?? 0.15) * RESIDENT_BOARD.h
        }}
        onStart={(e, data) => { startRef.current = { x: data.x, y: data.y }; }}
        onStop={(e, data) => {
          const moved = Math.hypot(data.x - startRef.current.x, data.y - startRef.current.y);
          if (moved > DRAG_THRESHOLD) onDragStopRV(note, data);
          else beginEdit(note); // a click (not a drag) opens the editor
        }}
      >
        <div
          className={cls}
          title="Click to edit • drag to move — this is exactly what the resident sees"
          style={{
            backgroundColor: note.color,
            fontSize: vhPx(fh),
            padding: `${vhPx(1.2)}px ${vhPx(1.6)}px`,
            maxWidth: 0.4 * TABLET_PANEL.w
          }}
        >
          {note.alert && note.actionAt != null && (
            <div className="reminder-time">⏰ {formatAlarm(note, tz)}</div>
          )}
          {note.item}
          {offscreen && <div className="rv-offscreen-tag">⏳ {scheduleLabel(note, now, tz)}</div>}
          {note.authorName && <div className="note-from-display">— {note.authorName}</div>}
        </div>
      </Draggable>
    );
  };

  if (!boardId) {
    return (
      <div className="cg-board-empty">
        Select or add a resident to manage their board.
      </div>
    );
  }

  return (
    <>
      {isStale && (
        <div className="stale-banner" role="status">
          ⏳ No new messages or photos in {staleDays} day{staleDays === 1 ? "" : "s"} —
          post something to brighten {residentName ? `${residentName}'s` : "their"} day.
        </div>
      )}
      <div className={"messageBox" + (composeOpen ? " mb-open" : "")}>
        <button
          type="button"
          className="mb-reminder-toggle"
          onClick={() => {
            const turningOn = !reminderOn;
            setReminderOn(turningOn);
            // Default "Show from" to a minute out (resident tz) so the message
            // doesn't appear while the schedule is still being set.
            if (turningOn && !actionLocal) setActionLocal(instantToWallClock(Date.now() + SCHEDULE_LEAD_MS, tz));
          }}
          title="Schedule when this message shows (from / for / repeat)"
          style={{ background: reminderOn ? "#f0ad2e" : undefined, flex: "0 0 auto", fontSize: "2rem", lineHeight: 1, padding: "2px 10px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          📅
        </button>
        <input
          id="mb-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder={reminderOn ? "Event text…" : "Type a message and press Enter…"}
          title="Type a note for the resident, then press Enter or ADD"
        />
        <button onClick={addNote} title="Post this message to the resident's board">ADD</button>
        <button
          type="button"
          className="mb-photo-btn"
          onClick={() => fileRef.current && fileRef.current.click()}
          title="Add a photo to the slideshow"
          style={{ flex: "0 0 auto", fontSize: "2rem", lineHeight: 1, padding: "2px 10px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          <span style={{ display: "inline-block", transform: "translateY(-0.14em)" }}>📷</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={onPickPhoto} style={{ display: "none" }} />
        {photoErr && <div style={{ flexBasis: "100%", color: "var(--danger-ink)", fontSize: "0.8rem" }}>{photoErr}</div>}
        {reminderOn && (
          <div className="mb-reminder-row" style={{ flexBasis: "100%", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", fontSize: "0.85rem" }}>
            <span>Show from</span>
            <input type="datetime-local" value={actionLocal}
              onChange={(e) => setActionLocal(e.target.value)}
              style={{ flex: "0 0 auto", width: "auto" }} />
            <span>· for</span>
            <select value={durationKey} onChange={(e) => setDurationKey(e.target.value)}
              style={{ flex: "0 0 auto", width: "auto" }}>
              {DURATIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <span>· repeat</span>
            <select value={recur} onChange={(e) => setRecur(e.target.value)}
              style={{ flex: "0 0 auto", width: "auto" }}>
              <option value="once">once</option>
              <option value="daily">daily</option>
              <option value="weekly">weekly</option>
            </select>
            {recur === "weekly" && (
              <span style={{ display: "inline-flex", gap: "2px" }}>
                {WEEKDAYS.map((d, i) => (
                  <button type="button" key={d}
                    onClick={() => setRecurDays((a) => toggleDay(a, i))}
                    className={"mb-day" + (recurDays.includes(i) ? " on" : "")}
                    title={d}>{d[0]}</button>
                ))}
              </span>
            )}
            <label style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
              title="Make this an alarm: it glows before the event time, then greys out after">
              <input type="checkbox" checked={alertOn} onChange={(e) => {
                const on = e.target.checked; setAlertOn(on);
                if (on && !eventLocal) setEventLocal(actionLocal || instantToWallClock(Date.now(), tz));
              }} />
              alarm
            </label>
            {alertOn && (
              <>
                <span>· event at</span>
                <input type="datetime-local" value={eventLocal}
                  onChange={(e) => setEventLocal(e.target.value)}
                  title="When the event happens — the message greys out at this time"
                  style={{ flex: "0 0 auto", width: "auto" }} />
                <span>· glow</span>
                <input type="number" min="0" value={warnQty}
                  onChange={(e) => setWarnQty(e.target.value)}
                  title="How long before the event the border starts glowing"
                  style={{ flex: "0 0 auto", width: "4em" }} />
                <select value={warnUnit} onChange={(e) => setWarnUnit(e.target.value)}
                  style={{ flex: "0 0 auto", width: "auto" }}>
                  <option value="minutes">min</option>
                  <option value="hours">hr</option>
                </select>
                <span>before</span>
              </>
            )}
            <span>({tz})</span>
          </div>
        )}
      </div>

      <div className="rv-hintbar">
        <span className="rv-conn" title={cellular
          ? "This screen connects to the internet over cellular"
          : "This screen connects to the internet over Wi-Fi"}>
          {cellular ? "📶 Cellular" : "📶 Wi-Fi"}
        </span>
        <span className="rv-hint-text">
          Dimmed notes are scheduled — they'll show on the resident's screen at their set time.
        </span>
      </div>

      {editPhoto && (
        <div className="mui mui-overlay" onClick={closePhotoDialog}>
          <div className="mui-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mui-head">
              <h3 className="mui-title">Photo</h3>
              <button className="mui-close" onClick={closePhotoDialog} aria-label="Close">×</button>
            </div>
            <img src={editPhoto.url} alt="" className="cg-delete-preview" />
            {editPhoto.addedByName && <p className="mui-sub">Added by {editPhoto.addedByName}</p>}
            <h4 className="mui-h4">Caption (optional)</h4>
            <input
              className="mui-field"
              value={captionDraft}
              onChange={(e) => setCaptionDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveCaption()}
              placeholder="e.g. Your daughter Sarah"
              maxLength={120}
              autoFocus
            />
            <div className="mui-inline" style={{ justifyContent: "space-between", marginTop: "1.1rem" }}>
              <button className="mui-btn mui-btn-danger" onClick={deleteThisPhoto}>Delete photo</button>
              <span className="mui-inline">
                <button className="mui-btn mui-btn-outline" onClick={closePhotoDialog}>Cancel</button>
                <button className="mui-btn mui-btn-primary" onClick={saveCaption}>Save caption</button>
              </span>
            </div>
          </div>
        </div>
      )}

      {editingNote && (
        <div className="mui mui-overlay" onClick={cancelEdit}>
          <div className="mui-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mui-head">
              <h3 className="mui-title">Edit message</h3>
              <button className="mui-close" onClick={cancelEdit} aria-label="Close">×</button>
            </div>
            <textarea
              className="mui-field"
              rows={3}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveEdit(editingNote); }}
              autoFocus
            />
            <label className="mui-check" style={{ marginTop: "0.9rem" }}>
              <input type="checkbox" checked={editReminderOn}
                onChange={(e) => {
                  const on = e.target.checked;
                  setEditReminderOn(on);
                  // Default "Show from" to a minute out (resident tz) so the message
                  // doesn't appear while the schedule is still being set.
                  if (on && !editActionLocal) setEditActionLocal(instantToWallClock(Date.now() + SCHEDULE_LEAD_MS, tz));
                }} />
              Schedule this message
            </label>
            {editReminderOn && (
              <div className="mui-inline" style={{ flexWrap: "wrap", alignItems: "center", gap: "0.5rem", marginTop: "0.6rem" }}>
                <span className="mui-muted">Show from</span>
                <input className="mui-field" style={{ width: "auto", flex: "0 0 auto" }}
                  type="datetime-local" value={editActionLocal}
                  onChange={(e) => setEditActionLocal(e.target.value)} />
                <span className="mui-muted">· for</span>
                <select className="mui-field" style={{ width: "auto", flex: "0 0 auto" }}
                  value={editDurationKey} onChange={(e) => setEditDurationKey(e.target.value)}>
                  {DURATIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
                <span className="mui-muted">· repeat</span>
                <select className="mui-field" style={{ width: "auto", flex: "0 0 auto" }}
                  value={editRecur} onChange={(e) => setEditRecur(e.target.value)}>
                  <option value="once">once</option>
                  <option value="daily">daily</option>
                  <option value="weekly">weekly</option>
                </select>
                {editRecur === "weekly" && (
                  <span style={{ display: "inline-flex", gap: "2px" }}>
                    {WEEKDAYS.map((d, i) => (
                      <button type="button" key={d}
                        onClick={() => setEditRecurDays((a) => toggleDay(a, i))}
                        className={"mb-day" + (editRecurDays.includes(i) ? " on" : "")}
                        title={d}>{d[0]}</button>
                    ))}
                  </span>
                )}
                <label className="mui-check" style={{ margin: 0 }}
                  title="Make this an alarm: it glows before the event time, then greys out after">
                  <input type="checkbox" checked={editAlertOn} onChange={(e) => {
                    const on = e.target.checked; setEditAlertOn(on);
                    if (on && !editEventLocal) setEditEventLocal(editActionLocal || instantToWallClock(Date.now(), tz));
                  }} />
                  alarm
                </label>
                {editAlertOn && (
                  <>
                    <span className="mui-muted">· event at</span>
                    <input className="mui-field" style={{ width: "auto", flex: "0 0 auto" }}
                      type="datetime-local" value={editEventLocal}
                      title="When the event happens — the message greys out at this time"
                      onChange={(e) => setEditEventLocal(e.target.value)} />
                    <span className="mui-muted">· glow</span>
                    <input className="mui-field" style={{ width: "4em", flex: "0 0 auto" }}
                      type="number" min="0" value={editWarnQty}
                      title="How long before the event the border starts glowing"
                      onChange={(e) => setEditWarnQty(e.target.value)} />
                    <select className="mui-field" style={{ width: "auto", flex: "0 0 auto" }}
                      value={editWarnUnit} onChange={(e) => setEditWarnUnit(e.target.value)}>
                      <option value="minutes">min</option>
                      <option value="hours">hr</option>
                    </select>
                    <span className="mui-muted">before</span>
                  </>
                )}
                <span className="mui-muted">({tz})</span>
              </div>
            )}
            {editingNote.authorName && (
              <p className="mui-sub" style={{ marginTop: "0.8rem" }}>from {editingNote.authorName}</p>
            )}
            <div className="mui-inline" style={{ justifyContent: "space-between", marginTop: "1.1rem" }}>
              <button className="mui-btn mui-btn-danger" onClick={() => removeNote(editingNote)}>Delete</button>
              <span className="mui-inline">
                <button className="mui-btn mui-btn-outline" onClick={cancelEdit}>Cancel</button>
                <button className="mui-btn mui-btn-primary" onClick={() => saveEdit(editingNote)}>Save</button>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* The board: the tablet's logical board, CSS-scaled to fit. Drag to move,
          click to edit — always exactly what the resident sees. */}
      <div className="rv-frame" ref={rvRef} style={{ height: RESIDENT_BOARD.h * rvK }}>
        <div
          className="rv-canvas"
          style={{
            width: RESIDENT_BOARD.w,
            height: RESIDENT_BOARD.h,
            transform: `scale(${rvK})`,
            transformOrigin: "top left"
          }}
        >
          {shownPhotoActive && (
            <div className="display-photo-panel">
              <PhotoSlideshow photos={photos} imgClassName="display-photo-img"
                paused={!!editPhoto} onSlideClick={openPhotoDialog} />
            </div>
          )}
          {/* Clock (behind the notes) exactly as the resident would see it; any
              dimmed/scheduled notes render on top at their eventual positions. */}
          {clockMode && (
            <div className={"display-clock display-clock-" + clockMode}
              style={{ fontSize: vhPx(CLOCK_TIME_VH[clockMode]) }}>
              <BigClock tz={tz} />
            </div>
          )}
          {items.map(renderRvNote)}
        </div>
      </div>
    </>
  );
};

export default CaregiverBoard;
